import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { ToWords } from 'to-words';
import logo from '../../../public/logo.png'
// Register fonts if needed
// Font.register({ family: 'Roboto', src: 'https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf' });

// Color palette
const colors = {
  primary: '#4F46E5',    // Indigo
  secondary: '#10B981',  // Emerald
  dark: '#111827',       // Gray 900
  light: '#6B7280',      // Gray 500
  border: '#E5E7EB'      // Gray 200
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    backgroundColor: '#F9FAFB'
  },
  container: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 30,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingBottom: 20
  },
  logo: {
    width: 120,
    height: 40,
    objectFit:'contain',
    marginBottom: 10
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 5
  },
  subtitle: {
    fontSize: 12,
    color: colors.light
  },
  section: {
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.dark,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingBottom: 5
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  label: {
    fontSize: 12,
    color: colors.light,
    width: '40%'
  },
  value: {
    fontSize: 12,
    color: colors.dark,
    width: '60%',
    textAlign: 'right'
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.dark
  },
  totalValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary
  },
  footer: {
    marginTop: 30,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    fontSize: 10,
    color: colors.light,
    textAlign: 'center'
  },
  amountInWords: {
    marginTop: 15,
    padding: 10,
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
    fontSize: 11,
    color: colors.dark
  }
});

const toWords = new ToWords({
  localeCode: 'en-IN',
  converterOptions: {
    currency: true,
    ignoreDecimal: false,
    ignoreZeroCurrency: false,
    doNotAddOnly: false,
    currencyOptions: {
      name: 'Rupee',
      plural: 'Rupees',
      symbol: '₹',
      fractionalUnit: {
        name: 'Paisa',
        plural: 'Paise',
        symbol: '',
      },
    }
  }
});

const HistoryPdf = ({ pdfData, createdAt, transaction_id, merchant }) => {
  const amountInWords = toWords.convert(pdfData.grand_total);
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <View>
              {/* Replace with your logo */}
              <Image style={styles.logo} src={logo} />
              <Text style={styles.title}>INVOICE</Text>
              <Text style={styles.subtitle}>#{transaction_id}</Text>
            </View>
            
            <View>
              <Text style={styles.subtitle}>Date: {dayjs(createdAt).format('DD MMM, YYYY')}</Text>
              <Text style={styles.subtitle}>Due Date: {dayjs(createdAt).add(7, 'day').format('DD MMM, YYYY')}</Text>
            </View>
          </View>
          
          {/* From/To Section */}
          <View style={[styles.section, { flexDirection: 'row', justifyContent: 'space-between' }]}>
            <View style={{ width: '48%' }}>
              <Text style={styles.sectionTitle}>Bill To</Text>
              <Text style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 5 }}>{merchant?.business_name}</Text>
              <Text style={{ fontSize: 10, color: colors.light, marginBottom: 3 }}>{merchant?.vendorProfile?.address}</Text>
              <Text style={{ fontSize: 10, color: colors.light, marginBottom: 3 }}>{merchant?.vendorProfile?.city}, {merchant?.vendorProfile?.state}</Text>
              <Text style={{ fontSize: 10, color: colors.light }}>GSTIN: {merchant?.vendorProfile?.gst_no || 'N/A'}</Text>
            </View>
            
            <View style={{ width: '48%' }}>
              <Text style={styles.sectionTitle}>From</Text>
              <Text style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 5 }}>Event Planet</Text>
              <Text style={{ fontSize: 10, color: colors.light }}>support@eventplanet.in</Text>
            </View>
          </View>
          
          {/* Invoice Details */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Invoice Details</Text>
            
            <View style={styles.row}>
              <Text style={styles.label}>Description</Text>
              <Text style={styles.label}>Amount (₹)</Text>
            </View>
            
            <View style={styles.row}>
              <Text style={styles.value}>Lead Charge</Text>
              <Text style={styles.value}>{pdfData.base_price.toFixed(2)}</Text>
            </View>
            
            <View style={styles.row}>
              <Text style={styles.value}>CGST (9%)</Text>
              <Text style={styles.value}>{pdfData.cgst.toFixed(2)}</Text>
            </View>
            
            <View style={styles.row}>
              <Text style={styles.value}>SGST (9%)</Text>
              <Text style={styles.value}>{pdfData.sgst.toFixed(2)}</Text>
            </View>
            
            {pdfData.discount > 0 && (
              <View style={styles.row}>
                <Text style={styles.value}>Discount</Text>
                <Text style={[styles.value, { color: colors.secondary }]}>-{pdfData.discount.toFixed(2)}</Text>
              </View>
            )}
            
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Amount</Text>
              <Text style={styles.totalValue}>₹{pdfData.grand_total.toFixed(2)}</Text>
            </View>
          </View>
          
          {/* Amount in words */}
          <View style={styles.amountInWords}>
            <Text>Amount in words: {amountInWords}</Text>
          </View>
          
          {/* Payment Info */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment Information</Text>
            <Text style={{ fontSize: 10, marginBottom: 3 }}>Payment Method: Razorpay</Text>
            <Text style={{ fontSize: 10 }}>Transaction ID: {transaction_id}</Text>
          </View>
          
          {/* Footer */}
          <View style={styles.footer}>
            <Text>Thank you for your business!</Text>
            <Text>For any inquiries, please contact {merchant?.vendorProfile?.email || merchant?.email}</Text>
            <Text style={{ marginTop: 5 }}>This is a computer generated invoice and does not require a signature.</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default HistoryPdf;